import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Card, CardContent, Grid, TextField, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, Chip } from '@mui/material';
import { AccountBalance } from '@mui/icons-material';
import ImportExportBar from '../../components/ImportExport/ImportExportBar';
import { onDataUpdated } from '../../utils/dataBus';

interface BankTx { id: string; date: string; libelle: string; debit: number; credit: number }
interface Ligne { debit: number; credit: number }
interface Ecriture { id: string; date_ecriture: string; libelle: string; montant_total: number; lignes: Ligne[] }

const STORAGE_ECRITURES = 'comptaEBNL_ecritures_local';
const STORAGE_BANK = 'comptaEBNL_bank_txs';

const Rapprochement: React.FC = () => {
  const [ecritures, setEcritures] = useState<Ecriture[]>([]);
  const [bankTxs, setBankTxs] = useState<BankTx[]>([]);
  const [tolDays, setTolDays] = useState<number>(3);
  const [tolAmount, setTolAmount] = useState<number>(1); // en unité monétaire

  const loadAll = () => {
    try { setEcritures(JSON.parse(localStorage.getItem(STORAGE_ECRITURES) || '[]')); } catch { setEcritures([]); }
    try { setBankTxs(JSON.parse(localStorage.getItem(STORAGE_BANK) || '[]')); } catch { setBankTxs([]); }
  };

  useEffect(() => {
    loadAll();
    const off = onDataUpdated(loadAll);
    return off;
  }, []);

  const parseCSV = (text: string): Record<string, string>[] => {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length === 0) return [];
    const headers = lines[0].split(',').map(h => h.trim());
    return lines.slice(1).map(line => {
      const cols = line.split(',');
      const row: Record<string, string> = {};
      headers.forEach((h, i) => { row[h] = (cols[i] || '').trim(); });
      return row;
    });
  };

  const onImportCSV = (csvText: string) => {
    // Columns: date,libelle,debit,credit
    const rows = parseCSV(csvText);
    const list: BankTx[] = rows.map((r, idx) => ({
      id: `${Date.now()}_${idx}`,
      date: r.date || new Date().toISOString().slice(0,10),
      libelle: r.libelle || '',
      debit: Number(r.debit) || 0,
      credit: Number(r.credit) || 0,
    }));
    localStorage.setItem(STORAGE_BANK, JSON.stringify(list));
    setBankTxs(list);
  };

  const getExportCSV = () => {
    const header = 'date,libelle,debit,credit';
    const lines = [header, ...bankTxs.map(t => [t.date, t.libelle.replace(/,/g,' '), t.debit.toFixed(2), t.credit.toFixed(2)].join(','))];
    return { filename: 'releves_bancaires.csv', content: lines.join('\n') };
  };
  const getExportJSON = () => ({ filename: 'releves_bancaires.json', content: JSON.stringify(bankTxs, null, 2) });

  const suggestions = useMemo(() => {
    const result: { tx: BankTx; ecriture?: Ecriture; ecart?: number }[] = [];
    const parseDate = (d: string) => new Date(d + 'T00:00:00Z');
    for (const tx of bankTxs) {
      const amount = Math.abs(tx.debit - tx.credit);
      let best: { e: Ecriture; ecart: number } | null = null;
      for (const e of ecritures) {
        const deltaDays = Math.abs((parseDate(tx.date).getTime() - parseDate(e.date_ecriture).getTime()) / 86400000);
        if (deltaDays > tolDays) continue;
        const ecart = Math.abs((e.montant_total || 0) - amount);
        if (ecart <= tolAmount && (!best || ecart < best.ecart)) {
          best = { e, ecart };
        }
      }
      result.push({ tx, ecriture: best?.e, ecart: best?.ecart });
    }
    return result;
  }, [bankTxs, ecritures, tolDays, tolAmount]);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Rapprochement Bancaire
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Paramétrage et suggestions automatiques
        </Typography>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalance color="primary" />
            <Typography variant="h6">Relevés bancaires</Typography>
          </Box>
          <ImportExportBar onImportCSV={onImportCSV} getExportCSV={getExportCSV} getExportJSON={getExportJSON} />
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <TextField fullWidth type="number" label="Tolérance jours" value={tolDays} onChange={e => setTolDays(Number(e.target.value))} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth type="number" label="Tolérance montant" value={tolAmount} onChange={e => setTolAmount(Number(e.target.value))} />
        </Grid>
      </Grid>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Libellé</TableCell>
              <TableCell align="right">Débit</TableCell>
              <TableCell align="right">Crédit</TableCell>
              <TableCell>Suggestion</TableCell>
              <TableCell align="right">Écart</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {suggestions.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center">Aucune transaction importée</TableCell></TableRow>
            ) : suggestions.map((s, idx) => (
              <TableRow key={idx} hover>
                <TableCell>{s.tx.date}</TableCell>
                <TableCell>{s.tx.libelle}</TableCell>
                <TableCell align="right">{s.tx.debit.toFixed(2)}</TableCell>
                <TableCell align="right">{s.tx.credit.toFixed(2)}</TableCell>
                <TableCell>
                  {s.ecriture ? (
                    <Chip size="small" color="success" label={`${s.ecriture.date_ecriture} • ${s.ecriture.libelle}`} />
                  ) : (
                    <Chip size="small" label="Aucune" />
                  )}
                </TableCell>
                <TableCell align="right">{s.ecart !== undefined ? s.ecart.toFixed(2) : '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Rapprochement;